import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Timer as TimerIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

// Import services
import * as timeTrackingService from '../../../services/timeTrackingService';
import useNotificationStore from '../../../stores/notificationStore';
import useAuthStore from '../../../stores/authStore';

const TaskCardTimer = ({ task, onTimeUpdate, compact = true }) => {
  const theme = useTheme();
  const { showSuccess, showError } = useNotificationStore();
  const { user } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second if there's an active timer
  useEffect(() => {
    const hasActiveTimer = task?.timeEntries?.some(entry => entry.isActive);
    if (!hasActiveTimer) return;

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [task?.timeEntries]);

  // Get time entries from task
  const timeEntries = task?.timeEntries || [];

  // Find active timer for current user
  const myActiveTimer = useMemo(() => {
    return timeEntries.find(entry => {
      const entryUserId = entry.user?._id || entry.user;
      return entry.isActive && entryUserId === user?._id;
    });
  }, [timeEntries, user]);

  // Check if any user has an active timer
  const anyActiveTimer = useMemo(() => {
    return timeEntries.find(entry => entry.isActive);
  }, [timeEntries]);

  // Calculate my total time
  const myTotalTime = useMemo(() => {
    let total = 0;
    timeEntries.forEach(entry => {
      const entryUserId = entry.user?._id || entry.user;
      if (entryUserId === user?._id) {
        if (entry.isActive) {
          total += currentTime - new Date(entry.startTime).getTime();
        } else {
          total += entry.duration || 0;
        }
      }
    });
    return total;
  }, [timeEntries, currentTime, user]);

  // Calculate total task time
  const totalTaskTime = useMemo(() => {
    let total = 0;
    timeEntries.forEach(entry => {
      if (entry.isActive) {
        total += currentTime - new Date(entry.startTime).getTime();
      } else {
        total += entry.duration || 0;
      }
    });
    return total;
  }, [timeEntries, currentTime]);

  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return '0m';
    
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (compact) {
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    } else {
      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
  };

  const handleStartTimer = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      await timeTrackingService.startTimer(task._id);
      showSuccess('Timer started');
      
      if (onTimeUpdate) {
        await onTimeUpdate();
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to start timer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopTimer = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isLoading || !myActiveTimer) return;
    
    setIsLoading(true);
    try {
      await timeTrackingService.stopTimer(task._id);
      showSuccess('Timer stopped');
      
      if (onTimeUpdate) {
        await onTimeUpdate();
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to stop timer');
    } finally {
      setIsLoading(false);
    }
  };

  const getElapsedTime = () => {
    if (!myActiveTimer) return 0;
    return currentTime - new Date(myActiveTimer.startTime).getTime();
  };

  // Compact view for task cards
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Show timer button or active timer */}
        {myActiveTimer ? (
          // Active timer - show with stop button
          <Tooltip title="Click to stop timer">
            <Chip
              icon={<StopIcon sx={{ fontSize: 16 }} />}
              label={formatDuration(getElapsedTime())}
              size="small"
              color="success"
              onClick={handleStopTimer}
              sx={{
                cursor: 'pointer',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                  '100%': { opacity: 1 },
                },
                height: 24,
                '& .MuiChip-label': {
                  px: 0.8,
                  fontSize: '0.7rem',
                  fontWeight: 600
                },
                '&:hover': {
                  backgroundColor: theme.palette.success.dark
                }
              }}
            />
          </Tooltip>
        ) : (
          // No active timer - show start button
          <Tooltip title="Start timer">
            <IconButton
              size="small"
              onClick={handleStartTimer}
              disabled={isLoading || (anyActiveTimer && !myActiveTimer)}
              sx={{
                p: 0.5,
                color: theme.palette.action.active,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                },
                '&.Mui-disabled': {
                  color: theme.palette.action.disabled
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <PlayIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Timer for other users */}
        {!myActiveTimer && anyActiveTimer && (
          <Tooltip title={`${anyActiveTimer.user?.firstName || 'Someone'} is tracking time`}>
            <Chip
              icon={<TimerIcon sx={{ fontSize: 16 }} />}
              label="Active"
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                '& .MuiChip-label': {
                  px: 0.8,
                  fontSize: '0.7rem'
                }
              }}
            />
          </Tooltip>
        )}

        {/* Total time if no active timer */}
        {!anyActiveTimer && totalTaskTime > 0 && (
          <Tooltip title={`Your time: ${formatDuration(myTotalTime)}`}>
            <Chip
              icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
              label={formatDuration(totalTaskTime)}
              size="small"
              variant="outlined"
              color="default"
              sx={{
                height: 24,
                '& .MuiChip-label': {
                  px: 0.8,
                  fontSize: '0.7rem'
                }
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  // Full view (not used in cards)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
      {/* Timer display */}
      <Box sx={{ flex: 1 }}>
        {myActiveTimer ? (
          <Box>
            <Typography variant="caption" color="success.main" fontWeight="bold">
              Timer Active
            </Typography>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {formatDuration(getElapsedTime())}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Time
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatDuration(totalTaskTime)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Control button */}
      {myActiveTimer ? (
        <IconButton
          color="error"
          onClick={handleStopTimer}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : <StopIcon />}
        </IconButton>
      ) : (
        <IconButton
          color="success"
          onClick={handleStartTimer}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : <PlayIcon />}
        </IconButton>
      )}
    </Box>
  );
};

export default TaskCardTimer;